/**
 * Kiểm tra payload của API trang quán trước khi đưa vào màn hình.
 *
 * KHÔNG còn đổi tên field nào: từ 09/2026 backend trả đúng hình dạng trong
 * interfaces/venue.ts (xem sportbook-backend/src/modules/venues/venues.presenter.ts).
 * Lớp map cũ ở đây đã chuyển sang backend, vì client thứ hai - app mobile chẳng
 * hạn - lẽ ra phải viết lại y hệt nó.
 *
 * Còn lại đúng một việc: dữ liệu qua mạng có thể thiếu hoặc hỏng, và ở đây phải
 * trả null chứ không để một object dở dang đi tiếp rồi component nổ giữa lúc vẽ.
 */
import { FeaturedVenue, VenuePage } from "../interfaces/venue";
import { getAssetFullUrl } from "./utils";

const laMang = (value: unknown): boolean => Array.isArray(value);

/**
 * Backend trả đường dẫn tương đối của ảnh ("images/abc.jpg"), không phải URL.
 *
 * Đó là lệ chung của cả hệ thống: logo giải, ảnh tin tức, ảnh sự kiện đều vậy, và
 * mọi màn hình khác của front office đều gọi getAssetFullUrl trước khi đưa vào thẻ
 * img. Trang quán quên làm việc đó nên ảnh bìa, logo và ảnh bàn đều vỡ.
 *
 * Gắn ở đây chứ không rải trong component: một chỗ quên là một tấm ảnh vỡ, mà lỗi
 * đó chỉ nhìn mới thấy chứ không có test nào đỏ.
 */
const anh = (value: unknown): string | null =>
  typeof value === "string" ? getAssetFullUrl(value) ?? null : null;

/**
 * Payload của GET /public/venues/:id, hoặc null nếu không đủ để vẽ một trang.
 *
 * "Đủ" nghĩa là có khối `venue` kèm id, và ba danh sách đúng là mảng - đó là
 * những thứ màn hình đụng tới ngay khi render, trước cả khi người xem cuộn.
 */
export const toVenuePage = (raw: any): VenuePage | null => {
  if (!raw || !raw.venue || !raw.venue.id) {
    return null;
  }
  if (!laMang(raw.venue.amenities) || !laMang(raw.pricing) || !laMang(raw.promotions)) {
    return null;
  }

  const page = raw as VenuePage;

  return {
    ...page,
    venue: {
      ...page.venue,
      logo: anh(page.venue.logo),
      coverImage: anh(page.venue.coverImage),
    },
    pricing: page.pricing.map((item) => ({ ...item, image: anh(item.image) })),
  };
};

/**
 * Payload của GET /public/venues/featured.
 *
 * Bỏ qua thẻ hỏng thay vì bỏ cả khối: một quán thiếu id không nên làm năm quán
 * còn lại biến mất khỏi trang chủ.
 */
export const toFeaturedVenues = (raw: unknown): FeaturedVenue[] =>
  (laMang(raw) ? (raw as any[]) : [])
    .filter((venue) => venue && venue.id)
    .map((venue) => ({
      ...venue,
      logo: anh(venue.logo),
      coverImage: anh(venue.coverImage),
    })) as FeaturedVenue[];
